(() => {
  const ROOT_SELECTOR = "[data-morcart-payment-survey]";

  class MorcartPaymentSurvey {
    constructor(root) {
      this.root = root;
      this.screens = new Map(
        [...root.querySelectorAll("[data-mps-screen]")].map((screen) => [screen.dataset.mpsScreen, screen]),
      );
      this.currentScreen = root.querySelector("[data-mps-screen].is-active")?.dataset.mpsScreen || "payment";
      this.screenStack = [];
      this.transitionTimer = null;
      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      this.autoAdvance = this.reducedMotion ? 0 : Number(root.dataset.autoAdvance || 260);
      this.pendingStorageKey = `morcart_payment_survey_pending:${window.location.pathname}`;
      this.progressStorageKey = `morcart_payment_survey_progress:${window.location.pathname}`;
      this.hasExplicitReturnUrl = new URLSearchParams(window.location.search).has("return_to");
      this.isNativeEmailSubmit = false;
      this.captureCart = root.dataset.cartCapture === "true";
      this.cartUpdateUrl = root.dataset.cartUpdateUrl || "";
      this.anonymousReplyAddress = root.dataset.anonymousReplyAddress || "";
      this.submitting = false;
      this.announcer = root.querySelector("[data-mps-announcer]");
      this.emailForm = root.querySelector(".mps-email-form");
      this.emailInput = root.querySelector("[data-mps-email-input]");
      this.emailError = root.querySelector("[data-mps-email-error]");
      this.answers = {
        method: "",
        methodLabel: "",
        reason: "",
        reasonLabel: "",
        paypalDetail: "",
        paypalDetailLabel: "",
        note: "",
        syncStatus: "",
      };
      this.surveyId = this.getSurveyId();
      this.returnUrl = this.getSafeReturnUrl();
      this.restorePendingAnswers();
      this.placeAdvisors();

      this.bindChoiceButtons();
      this.bindConditionalForms();
      this.bindEmailForm();
      this.bindBackButtons();
      this.prepareReturnLinks();
      this.updateProgress(this.screens.get(this.currentScreen));
      this.restoreShopifyFormState();
      this.emit("ready");
    }

    placeAdvisors() {
      // Anchor artwork to the relevant control, outside its click/text area.
      for (const [name, selector] of [["payment", ".mps-choice-grid"], ["reason", ".mps-choice-grid"], ["paypal-problem", ".mps-choice-grid"], ["other-method", ".mps-field"], ["reason-other", ".mps-field"], ["email", ".mps-input-wrap"]]) {
        const screen = this.screens.get(name);
        const advisor = screen?.querySelector(".mps-advisor-inline");
        const anchor = screen?.querySelector(selector);
        if (advisor && anchor) {
          anchor.classList.add("mps-advisor-anchor", `mps-advisor-anchor--${name}`);
          anchor.prepend(advisor);
        }
      }
    }

    bindChoiceButtons() {
      this.root.querySelectorAll("[data-mps-choice-group]").forEach((group) => {
        group.addEventListener("click", (event) => {
          const button = event.target.closest(".mps-choice");
          if (!button || !group.contains(button)) return;
          this.selectChoice(group, button);
        });
      });
    }

    selectChoice(group, button) {
      window.clearTimeout(this.transitionTimer);
      group.querySelectorAll(".mps-choice").forEach((choice) => {
        const selected = choice === button;
        choice.classList.toggle("is-selected", selected);
        choice.setAttribute(
          "aria-label",
          selected ? `${choice.dataset.label || choice.textContent.trim()}, selected` : choice.dataset.label || choice.textContent.trim(),
        );
      });

      const field = group.dataset.mpsChoiceGroup;
      const value = button.dataset.value || "";
      const label = button.dataset.label || button.textContent.trim();
      this.clearDependentAnswers(field);
      this.answers[field] = value;
      this.answers[`${field}Label`] = label;
      this.announce(`${label} selected.`);
      this.emit("answer", { field, value, label });
      this.saveLocalPreference("in progress");

      this.transitionTimer = window.setTimeout(() => {
        if (field === "method") {
          if (value === "other") {
            this.showScreen("other-method");
          } else {
            this.prepareReasonScreen();
            this.showScreen("reason");
          }
          return;
        }

        if (field === "reason") {
          if (value === "paypal-failed") {
            this.showScreen("paypal-problem");
          } else if (value === "other") {
            this.showScreen("reason-other");
          } else {
            this.prepareEmailScreen();
            this.showScreen("email");
          }
          return;
        }

        if (field === "paypalDetail") {
          this.prepareEmailScreen();
          this.showScreen("email");
        }
      }, this.autoAdvance);
    }

    bindConditionalForms() {
      const otherMethodForm = this.root.querySelector("[data-mps-other-method-form]");
      const otherMethodInput = this.root.querySelector("[data-mps-other-method-input]");
      const skipOtherMethod = this.root.querySelector("[data-mps-skip-other-method]");

      otherMethodForm?.addEventListener("submit", (event) => {
        event.preventDefault();
        const label = otherMethodInput?.value.trim();
        this.clearDependentAnswers("method");
        this.answers.methodLabel = label || "Another payment method";
        this.answers.method = "other";
        this.prepareReasonScreen();
        this.emit("answer", {
          field: "methodDetail",
          value: this.answers.method,
          label: this.answers.methodLabel,
        });
        this.saveLocalPreference("in progress");
        this.showScreen("reason");
      });

      skipOtherMethod?.addEventListener("click", () => {
        this.clearDependentAnswers("method");
        this.answers.method = "other";
        this.answers.methodLabel = "Another payment method";
        this.saveLocalPreference("in progress");
        this.prepareReasonScreen();
        this.showScreen("reason");
      });

      const otherReasonForm = this.root.querySelector("[data-mps-reason-other-form]");
      const otherReasonInput = this.root.querySelector("[data-mps-reason-other-input]");
      const skipOtherReason = this.root.querySelector("[data-mps-skip-reason-other]");

      otherReasonForm?.addEventListener("submit", (event) => {
        event.preventDefault();
        this.answers.note = otherReasonInput?.value.trim() || "";
        this.emit("answer", { field: "note", value: this.answers.note });
        this.saveLocalPreference("in progress");
        this.prepareEmailScreen();
        this.showScreen("email");
      });

      skipOtherReason?.addEventListener("click", () => {
        this.answers.note = "";
        this.saveLocalPreference("in progress");
        this.prepareEmailScreen();
        this.showScreen("email");
      });
    }

    bindEmailForm() {
      if (!this.emailForm || !this.emailInput) return;

      this.emailInput.addEventListener("input", () => {
        const hasValue = this.emailInput.value.trim().length > 0;
        const isValid = hasValue && this.emailInput.validity.valid;
        this.emailForm.classList.toggle("is-valid", isValid);
        if (this.emailInput.getAttribute("aria-invalid") === "true") {
          this.clearEmailError();
        }
      });

      this.emailInput.addEventListener("blur", () => {
        if (this.emailInput.value.trim() && !this.emailInput.validity.valid) {
          this.showEmailError("Enter a valid email address, like name@example.com.");
        }
      });

      this.emailForm.addEventListener("submit", async (event) => {
        if (this.isNativeEmailSubmit) return;
        event.preventDefault();
        const email = this.emailInput.value.trim();

        if (!email || !this.emailInput.validity.valid) {
          this.showEmailError(
            email
              ? "Enter a valid email address, like name@example.com."
              : "Enter your email address, or choose “Skip & continue with PayPal.”",
          );
          this.emailInput.focus();
          return;
        }

        this.submitSurvey(email);
      });

      this.root.querySelector("[data-mps-skip-email]")?.addEventListener("click", () => {
        this.submitSurvey("");
      });
    }

    async submitSurvey(email) {
      if (this.submitting) return;
      this.clearEmailError();
      this.clearSaveError();
      if (!this.answers.method || !this.answers.reason) {
        this.showSaveError("Please choose a payment method and tell us why first.");
        return;
      }
      this.answers.emailProvided = Boolean(email);
      this.answers.syncStatus = "pending";
      this.savePendingAnswers();
      if (this.root.dataset.preview) {
        this.answers.syncStatus = "preview";
        this.prepareSuccessScreen(email);
        this.showScreen("success");
        return;
      }
      // Skip means no email is collected, even if the visitor typed one first.
      this.emailInput.value = email;
      this.submitting = true;
      const submitButton = this.emailForm.querySelector("[data-mps-email-submit]");
      submitButton?.setAttribute("aria-busy", "true");
      const label = submitButton?.querySelector("[data-mps-email-button-label]");
      const originalLabel = label?.textContent;
      if (label) label.textContent = "Sending your answer…";
      if (submitButton) submitButton.disabled = true;
      this.root.querySelector("[data-mps-skip-email]")?.setAttribute("disabled", "");
      try {
        await this.sendToCollector({
          event: "payment_survey_completed",
          method: this.answers.method,
          methodLabel: this.answers.methodLabel,
          reason: this.answers.reason,
          reasonLabel: this.answers.reasonLabel,
          paypalDetailLabel: this.answers.paypalDetailLabel || "",
          note: this.answers.note || "",
          email,
          noticeConsent: Boolean(email),
          sourceUrl: this.getCollectionSourceUrl(),
          website: this.emailForm.querySelector("[data-mps-honeypot]")?.value || "",
        });
        this.answers.syncStatus = "synced";
        this.savePendingAnswers();
        this.prepareSuccessScreen(email);
        this.showScreen("success");
        this.saveCartAttributes("received");
      } catch {
        this.showSaveError("We couldn’t confirm your answer was saved. Please try again — retries won’t create duplicate answers.");
      } finally {
        this.submitting = false;
        submitButton?.removeAttribute("aria-busy");
        if (submitButton) submitButton.disabled = false;
        this.root.querySelector("[data-mps-skip-email]")?.removeAttribute("disabled");
        if (label) label.textContent = originalLabel;
      }
    }

    getCollectionSourceUrl() {
      const source = new URL(this.returnUrl);
      const path = /^\/(products\/[a-zA-Z0-9_-]+|collections(?:\/[a-zA-Z0-9_-]+)?|cart|)\/?$/.test(source.pathname) ? source.pathname : "/cart";
      const clean = new URL(path, "https://morcart.com");
      const variant = source.searchParams.get("variant");
      if (variant && /^\d+$/.test(variant)) clean.searchParams.set("variant", variant);
      return clean.href;
    }

    async sendToCollector(payload, keepalive = false) {
      const endpoint = this.root.dataset.collectorUrl || "";
      if (!/^https:\/\/script\.google\.com\/macros\/s\/[A-Za-z0-9_-]+\/exec$/.test(endpoint)) throw new Error("Collector unavailable");
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 25000);
      try {
        const response = await fetch(endpoint, {
          method: "POST", credentials: "omit", redirect: "follow", keepalive,
          headers: {"Content-Type": "text/plain;charset=UTF-8"},
          signal: controller.signal,
          body: JSON.stringify({ ...payload, schemaVersion: 2, surveyId: this.surveyId, qaToken: this.root.dataset.qaToken || "" }),
        });
        if (!response.ok) throw new Error("Save failed");
        const result = await response.json();
        if (result.ok !== true || result.surveyId !== this.surveyId) throw new Error("Save unconfirmed");
        return result;
      } finally { window.clearTimeout(timeout); }
    }

    bindBackButtons() {
      this.root.querySelectorAll("[data-mps-step-back]").forEach((button) => {
        button.addEventListener("click", () => {
          window.clearTimeout(this.transitionTimer);
          const previous = this.screenStack.pop();
          if (previous) this.showScreen(previous, false);
        });
      });
    }

    prepareReasonScreen() {
      const label = this.answers.methodLabel || "that option";
      this.root.querySelectorAll("[data-mps-method-name]").forEach((node) => {
        node.textContent = label;
      });
      this.root.querySelectorAll("[data-mps-method-chip]").forEach((node) => {
        node.textContent = label.toUpperCase();
      });
    }

    prepareEmailScreen() {
      this.prepareReasonScreen();
    }

    prepareSuccessScreen(email) {
      const copy = this.root.querySelector("[data-mps-success-copy]");
      const status = this.root.querySelector("[data-mps-success-status-text]");
      const methodLabel = this.answers.methodLabel || "your choice";
      // Keep the concise title (including a merchant's configured title).
      if (status) {
        status.textContent = this.answers.syncStatus === "preview" ? "PREVIEW · NOT SENT" : "PREFERENCE RECEIVED";
      }
      if (!copy) return;

      if (email === true) {
        copy.textContent = "We’ll email you when your chosen payment method is available — with a big, exclusive discount just for you.";
      } else if (email) {
        copy.textContent = `We’ll email ${this.maskEmail(email)} when your chosen payment method is available — with a big, exclusive discount just for you.`;
      } else if (this.answers.syncStatus === "local") {
        copy.textContent = "We couldn’t send your answer. It’s kept in this browser for this session.";
      } else {
        copy.replaceChildren(
          document.createTextNode(`${methodLabel} is on your wish list.`),
          document.createElement("br"),
          document.createTextNode("Thanks for helping us improve checkout."),
        );
      }
    }

    restoreShopifyFormState() {
      // Only a server-acknowledged response saved in this session can restore success.
      // URL flags such as contact_posted=true are not proof of a Sheets submission.
      if (this.root.dataset.collectorUrl) {
        if (this.answers.syncStatus === "synced") {
          this.prepareSuccessScreen(this.answers.emailProvided === true);
          this.showScreen("success", false);
        } else if (this.answers.syncStatus === "pending") {
          this.prepareEmailScreen();
          this.showScreen("email", false);
          this.showSaveError("Please retry to confirm your answer was saved.");
        }
        return;
      }
      const posted = this.root.querySelector("[data-mps-email-posted-successfully]");
      const serverError = this.root.querySelector("[data-mps-email-server-error]");

      if (posted) {
        this.answers.syncStatus = "synced";
        this.prepareReasonScreen();
        this.prepareSuccessScreen(this.answers.emailProvided === true);
        this.savePendingAnswers();
        this.saveCartAttributes("received");
        this.showScreen("success", false);
        return;
      }

      if (serverError) {
        this.screenStack = ["payment", "reason"];
        this.prepareReasonScreen();
        this.prepareEmailScreen();
        this.showScreen("email", false);
        const message = serverError.textContent.trim() || "We couldn’t send your answer. Please try again.";
        this.showSaveError(message);
      } else if (this.answers.syncStatus === "pending" && this.answers.method && this.answers.reason) {
        this.prepareEmailScreen();
        this.showScreen("email", false);
        this.showSaveError("Your answer hasn’t been confirmed yet. Please submit again, or complete any verification shown.");
      }
    }

    showScreen(name, pushCurrent = true) {
      const nextScreen = this.screens.get(name);
      const current = this.screens.get(this.currentScreen);
      if (!nextScreen || nextScreen === current) return;

      if (pushCurrent && this.currentScreen) this.screenStack.push(this.currentScreen);
      current?.classList.remove("is-active", "is-entering");
      if (current) current.hidden = true;

      nextScreen.hidden = false;
      nextScreen.classList.add("is-active", "is-entering");
      this.currentScreen = name;
      this.updateProgress(nextScreen);

      const heading = nextScreen.querySelector("h1, h2");
      if (heading) {
        heading.setAttribute("tabindex", "-1");
        window.requestAnimationFrame(() => heading.focus({ preventScroll: true }));
      }

      const step = nextScreen.dataset.step || "";
      this.announce(name === "success" ? "Survey complete." : `Step ${step} of 3.`);
      this.emit("screen", { screen: name, step });
    }

    updateProgress(screen) {
      if (!screen) return;
      const isSuccess = screen.dataset.mpsScreen === "success";
      const step = Number(screen.dataset.step || 1);
      const label = this.root.querySelector("[data-mps-progress-label]");
      const progress = this.root.querySelector(".mps-progress");
      const segments = this.root.querySelectorAll("[data-mps-progress-segment]");

      if (label) label.textContent = isSuccess ? "Complete" : `${step} / 3`;
      progress?.setAttribute("role", "progressbar");
      progress?.setAttribute("aria-valuemin", "1");
      progress?.setAttribute("aria-valuemax", "3");
      progress?.setAttribute("aria-valuenow", String(step));
      progress?.setAttribute("aria-valuetext", isSuccess ? "Survey complete" : `Step ${step} of 3`);
      segments.forEach((segment, index) => segment.classList.toggle("is-active", index < step));
    }

    prepareReturnLinks() {
      this.root.querySelectorAll("[data-mps-return-link], [data-mps-paypal-return]").forEach((link) => {
        link.href = this.returnUrl;
      });
      this.root.querySelectorAll("[data-mps-return-link], [data-mps-paypal-return], [data-mps-clear-session]").forEach((link) => {
        link.addEventListener("click", () => this.clearPendingAnswers(), { capture: true });
      });

      const paypalReturn = this.root.querySelector("[data-mps-paypal-return]");
      paypalReturn?.addEventListener("click", async (event) => {
        event.preventDefault();
        paypalReturn.setAttribute("aria-busy", "true");
        paypalReturn.querySelector("[data-mps-paypal-label]")?.replaceChildren("Returning to payment…");
        this.emit("return", { url: this.returnUrl });
        if (this.answers.syncStatus === "synced" && this.root.dataset.collectorUrl) {
          // Keep navigation usable even if analytics fails; report only recorded clicks.
          await Promise.race([
            this.sendToCollector({event: "paypal_return_clicked"}, true).catch(() => null),
            new Promise(resolve => window.setTimeout(resolve, 1500)),
          ]);
        }
        window.location.assign(this.returnUrl);
      });
    }

    getSafeReturnUrl() {
      const baseUrl = window.location.href;
      const defaultFallback = new URL("/cart", baseUrl);
      let fallback = defaultFallback;
      try {
        const configured = new URL(this.root.dataset.returnUrl || "/cart", baseUrl);
        if (configured.origin === window.location.origin && /^(https?:|file:)$/.test(configured.protocol)) {
          fallback = configured;
        }
      } catch {
        fallback = defaultFallback;
      }
      const requested = new URLSearchParams(window.location.search).get("return_to") || document.referrer;
      if (!requested) return fallback.href;

      try {
        const candidate = new URL(requested, baseUrl);
        const safeProtocol = /^(https?:|file:)$/.test(candidate.protocol);
        const isSurvey = candidate.pathname === window.location.pathname;
        return safeProtocol && candidate.origin === window.location.origin && (!isSurvey || this.root.dataset.preview) ? candidate.href : fallback.href;
      } catch {
        return fallback.href;
      }
    }

    getSubmissionReturnPath() {
      const surveyUrl = new URL(window.location.href);
      // A custom return_to is used by Shopify only after an accepted contact submission.
      // Unlike "back", Shopify does not append its contact success flag to this URL.
      surveyUrl.searchParams.set("contact_posted", "true");
      surveyUrl.searchParams.delete("form_type");
      surveyUrl.hash = "";
      return surveyUrl.pathname + surveyUrl.search;
    }

    savePendingAnswers() {
      try {
        window.sessionStorage.setItem(
          this.pendingStorageKey,
          JSON.stringify({
            method: this.answers.method,
            methodLabel: this.answers.methodLabel,
            reason: this.answers.reason,
            reasonLabel: this.answers.reasonLabel,
            paypalDetail: this.answers.paypalDetail,
            paypalDetailLabel: this.answers.paypalDetailLabel,
            note: this.answers.note,
            syncStatus: this.answers.syncStatus,
            emailProvided: this.answers.emailProvided === true,
            surveyId: this.surveyId,
            returnUrl: this.returnUrl,
            savedAt: Date.now(),
          }),
        );
      } catch {
        // The native contact form still submits when session storage is unavailable.
      }
    }

    restorePendingAnswers() {
      try {
        const raw = window.sessionStorage.getItem(this.pendingStorageKey);
        const pending = JSON.parse(raw || "null");
        const hasServerResult = Boolean(
          this.root.querySelector("[data-mps-email-posted-successfully], [data-mps-email-server-error]"),
        );
        const isFresh = pending?.savedAt && Date.now() - Number(pending.savedAt) < 30 * 60 * 1000;

        if (this.hasExplicitReturnUrl && !hasServerResult && pending?.returnUrl !== this.returnUrl) {
          window.sessionStorage.removeItem(this.pendingStorageKey);
          return;
        }

        if (pending && typeof pending === "object" && isFresh) {
          const answerKeys = [
            "method",
            "methodLabel",
            "reason",
            "reasonLabel",
            "paypalDetail",
            "paypalDetailLabel",
            "note",
            "syncStatus",
          ];
          answerKeys.forEach((key) => {
            if (typeof pending[key] === "string") this.answers[key] = pending[key];
          });
          this.answers.emailProvided = pending.emailProvided === true;
          if (pending.surveyId) this.surveyId = pending.surveyId;
          if (pending.returnUrl && !this.hasExplicitReturnUrl) {
            const savedReturn = new URL(pending.returnUrl, window.location.href);
            if (savedReturn.origin === window.location.origin && /^(https?:|file:)$/.test(savedReturn.protocol)) {
              this.returnUrl = savedReturn.href;
            }
          }
        } else if (raw) {
          window.sessionStorage.removeItem(this.pendingStorageKey);
        }
      } catch {
        // Ignore malformed or unavailable session storage.
      }
    }

    clearPendingAnswers() {
      try {
        window.sessionStorage.removeItem(this.pendingStorageKey);
        window.sessionStorage.removeItem(this.progressStorageKey);
        window.sessionStorage.removeItem("morcart_payment_survey_id");
      } catch {
        // Nothing else is required.
      }
    }

    async saveCartAttributes(status) {
      this.saveLocalPreference(status);
      if (!this.captureCart || !this.cartUpdateUrl || this.root.dataset.preview) return null;

      const attributes = {
        "MORCART payment preference__": this.answers.methodLabel || "",
        "MORCART preference reason__": this.answers.reasonLabel || "",
        "MORCART PayPal issue__": this.answers.paypalDetailLabel || "",
        "MORCART survey note__": this.answers.note || "",
        "MORCART survey source__": new URL(this.returnUrl).pathname,
        "MORCART survey id__": this.surveyId,
        "MORCART survey status__": status,
      };

      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 2500);
      try {
        const response = await fetch(this.cartUpdateUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({ attributes }),
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Cart update returned ${response.status}`);
        return true;
      } catch (error) {
        this.emit("error", { type: "cart", message: error.message });
        return false;
      } finally {
        window.clearTimeout(timeout);
      }
    }

    saveLocalPreference(status) {
      try {
        window.sessionStorage.setItem(
          this.progressStorageKey,
          JSON.stringify({
            payment_method: this.answers.methodLabel,
            reason: this.answers.reasonLabel,
            paypal_issue: this.answers.paypalDetailLabel,
            note: this.answers.note,
            survey_id: this.surveyId,
            status,
            updated_at: new Date().toISOString(),
            expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
          }),
        );
      } catch {
        // Storage can be unavailable in strict privacy modes; the survey still works.
      }
    }

    clearDependentAnswers(field) {
      if (field === "method") {
        this.answers.reason = "";
        this.answers.reasonLabel = "";
        this.answers.paypalDetail = "";
        this.answers.paypalDetailLabel = "";
        this.answers.note = "";
        this.clearChoiceGroup("reason");
        this.clearChoiceGroup("paypalDetail");
      } else if (field === "reason") {
        this.answers.paypalDetail = "";
        this.answers.paypalDetailLabel = "";
        this.answers.note = "";
        this.clearChoiceGroup("paypalDetail");
      }
    }

    clearChoiceGroup(field) {
      const group = this.root.querySelector(`[data-mps-choice-group="${field}"]`);
      group?.querySelectorAll(".mps-choice").forEach((choice) => {
        choice.classList.remove("is-selected");
        choice.setAttribute("aria-label", choice.dataset.label || choice.textContent.trim());
      });
    }

    getSurveyId() {
      try {
        const existing = window.sessionStorage.getItem("morcart_payment_survey_id");
        if (existing) return existing;
        const generated = window.crypto?.randomUUID?.() || `mps-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        window.sessionStorage.setItem("morcart_payment_survey_id", generated);
        return generated;
      } catch {
        return `mps-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }
    }

    showEmailError(message) {
      if (!this.emailInput || !this.emailError) return;
      this.emailInput.setAttribute("aria-invalid", "true");
      this.emailError.lastChild.textContent = ` ${message}`;
      this.emailError.hidden = false;
      this.emailError.setAttribute("role", "alert");
      this.emailForm?.classList.remove("is-valid");
    }

    clearEmailError() {
      this.emailInput?.removeAttribute("aria-invalid");
      if (this.emailError) {
        this.emailError.hidden = true;
        this.emailError.removeAttribute("role");
      }
    }

    showSaveError(message) {
      const error = this.root.querySelector("[data-mps-save-error]");
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
      error.setAttribute("role", "alert");
    }

    clearSaveError() {
      const error = this.root.querySelector("[data-mps-save-error]");
      if (!error) return;
      error.hidden = true;
      error.removeAttribute("role");
    }

    announce(message) {
      if (!this.announcer) return;
      this.announcer.textContent = "";
      window.requestAnimationFrame(() => {
        this.announcer.textContent = message;
      });
    }

    emit(name, detail = {}) {
      this.root.dispatchEvent(
        new CustomEvent(`morcart:payment-survey:${name}`, {
          bubbles: true,
          detail: { ...detail, answers: { ...this.answers } },
        }),
      );
    }

    slugify(value) {
      return String(value || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 48);
    }

    maskEmail(email) {
      const [local, domain] = email.split("@");
      if (!domain) return email;
      const visible = local.slice(0, Math.min(2, local.length));
      return `${visible}***@${domain}`;
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.mpsInitialized === "true") return;
      root.dataset.mpsInitialized = "true";
      new MorcartPaymentSurvey(root);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize());
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
})();
